import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Database, Search, User, FileText, Heart, Pill, Activity, Calendar } from 'lucide-react';
import api from '../../api';

const EHRPage = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await api.get('/patients');
        if (response.data.success) {
          setPatients(response.data.patients);
          if (response.data.patients.length > 0) {
            setSelectedPatient(response.data.patients[0]);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, []);

  const filteredPatients = patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'vitals', label: 'Vitals', icon: Heart },
    { id: 'medications', label: 'Medications', icon: Pill },
    { id: 'visits', label: 'Visits', icon: Calendar },
    { id: 'labs', label: 'Lab Results', icon: Activity },
  ];

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-[#f39c12]" />
            Electronic Health Records
          </h1>
          <p className="text-gray-500 text-sm mt-1">Comprehensive patient health records and clinical documentation</p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Patient List */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search patients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${
                      selectedPatient?.id === patient.id ? 'bg-[#fff0e6] border-2 border-[#f39c12]' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#3498db] rounded-full flex items-center justify-center text-white font-medium">
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{patient.firstName} {patient.lastName}</div>
                        <div className="text-xs text-gray-500">{patient.status}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* EHR Content */}
          <div className="col-span-9">
            {selectedPatient ? (
              <div className="bg-white rounded-lg shadow-sm border">
                {/* Patient Header */}
                <div className="p-6 border-b bg-gradient-to-r from-[#3498db] to-[#2980b9] text-white rounded-t-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-[#3498db] text-2xl font-bold">
                      {selectedPatient.firstName?.[0]}{selectedPatient.lastName?.[0]}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{selectedPatient.firstName} {selectedPatient.lastName}</h2>
                      <p className="text-blue-100">DOB: 03/15/1945 | MRN: {selectedPatient.id?.slice(0, 8)}</p>
                      <p className="text-blue-100">{selectedPatient.primaryDiagnosis || 'No primary diagnosis'}</p>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b">
                  <div className="flex">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-6 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                          activeTab === tab.id
                            ? 'border-[#f39c12] text-[#f39c12]'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'summary' && (
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg">Patient Information</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-gray-500">Gender:</span><span>Male</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Insurance:</span><span>{selectedPatient.insuranceType || 'Medicare'}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Phone:</span><span>{selectedPatient.phone || '(555) 123-4567'}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Address:</span><span>{selectedPatient.address || '123 Main St'}</span></div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg">Care Team</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">Dr. Johnson - Primary Physician</span>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <User className="w-4 h-4 text-green-600" />
                            <span className="text-sm">Jennifer Davis, RN - Primary Nurse</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'vitals' && (
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Latest Vitals</h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-700">120/80</div>
                          <div className="text-sm text-gray-600">Blood Pressure</div>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-green-700">72</div>
                          <div className="text-sm text-gray-600">Heart Rate</div>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-yellow-700">98.6°F</div>
                          <div className="text-sm text-gray-600">Temperature</div>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-purple-700">98%</div>
                          <div className="text-sm text-gray-600">O2 Saturation</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'medications' && (
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Current Medications</h3>
                      <div className="space-y-3">
                        <div className="p-3 border rounded-lg flex items-center justify-between">
                          <div><span className="font-medium">Metformin 500mg</span><span className="text-gray-500 text-sm ml-2">- Twice daily with meals</span></div>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                        </div>
                        <div className="p-3 border rounded-lg flex items-center justify-between">
                          <div><span className="font-medium">Lisinopril 10mg</span><span className="text-gray-500 text-sm ml-2">- Once daily</span></div>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'visits' && (
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Recent Visits</h3>
                      <div className="space-y-3">
                        <div className="p-3 border rounded-lg"><span className="font-medium">SN Visit</span> - Jan 23, 2026 - Jennifer Davis, RN</div>
                        <div className="p-3 border rounded-lg"><span className="font-medium">PT Visit</span> - Jan 22, 2026 - Michael Brown, PT</div>
                        <div className="p-3 border rounded-lg"><span className="font-medium">SN Visit</span> - Jan 20, 2026 - Jennifer Davis, RN</div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'labs' && (
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Lab Results</h3>
                      <div className="space-y-3">
                        <div className="p-3 border rounded-lg flex items-center justify-between">
                          <div><span className="font-medium">HbA1c</span><span className="text-gray-500 text-sm ml-2">- Jan 15, 2026</span></div>
                          <span className="text-green-600 font-medium">6.5%</span>
                        </div>
                        <div className="p-3 border rounded-lg flex items-center justify-between">
                          <div><span className="font-medium">Blood Glucose</span><span className="text-gray-500 text-sm ml-2">- Jan 23, 2026</span></div>
                          <span className="font-medium">120 mg/dL</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
                Select a patient to view their health records
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EHRPage;
